import { useQuery, useReactiveVar } from "@apollo/client";
import {
  GetRoleFrameworkStatusQuery,
  GetRoleFrameworkStatusQueryVariables,
  OrganizationRole,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";

import getRoleFrameworkStatusQuery from "../../graphql/get-role-framework-status-query";
import { roleFrameworkChatUrl } from "./role-framework-helpers";

const RoleFrameworkCTA = ({ organizationId }: { organizationId: number }) => {
  const organization = useReactiveVar(currentOrganizationVar);
  const label = useLabel();
  // Ask AI operates in the current organization. Never launch another organization's setup there.
  const eligible =
    organization?.id === organizationId &&
    !!organization.featureFlags.askAi &&
    [OrganizationRole.GlobalAdmin, OrganizationRole.PeopleAdmin].includes(
      organization.userMembership?.role ?? OrganizationRole.Member,
    );
  const { data, loading, error } = useQuery<
    GetRoleFrameworkStatusQuery,
    GetRoleFrameworkStatusQueryVariables
  >(getRoleFrameworkStatusQuery, {
    variables: { organizationId },
    skip: !eligible,
    // `careerTracks` is cached under `keyArgs: ["organizationId"]` with a merge that
    // replaces `edges`, so writing this `first: 1` result would clobber the job
    // ladder's list on this same page. Keep the status check out of the cache.
    fetchPolicy: "no-cache",
  });
  if (!eligible || loading || error || !data) return null;
  const empty =
    !data.careerTracks.edges.length && !data.competencies.edges.length;
  return (
    <section
      aria-label="Build role framework"
      className={
        empty
          ? "rounded-lg border border-blue-200 bg-blue-50 p-6 mb-6"
          : "flex justify-end mb-6"
      }
    >
      {empty && (
        <>
          <h2 className="font-semibold mb-2">
            Build your role and {label("competency")} framework
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Answer a few questions about your people and their work. Topicflow
            AI will help you define roles, expectations, and employee
            assignments.
          </p>
        </>
      )}
      <Button
        text={empty ? "Build with Topicflow AI" : "Extend with Topicflow AI"}
        to={roleFrameworkChatUrl(label("competency"))}
      />
    </section>
  );
};

export default RoleFrameworkCTA;
